import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ConversationResponse } from '../api/conversation';
import { useAudioReply } from './AudioReplyContext';

/**
 * Auto-plays new assistant turns that arrive carrying an
 * `audio_url`. The decision of WHETHER to generate audio happens
 * at send-time (Composer / SendContext sets `audio_requested:
 * true` on the request when the user toggled audio replies on
 * OR when the message came in by voice). If the server returned
 * audio_url on the turn, we play it - no second gate here.
 *
 * Why subscribe to the cache instead of playing imperatively
 * from SendContext.onSuccess: the assistant turn's timestamp
 * (which the per-message stop button binds against via
 * AudioReplyContext.playingFor) is server-set and only known
 * after the conversation refetch lands. Routing playback
 * through the subscriber means the stop button on the right
 * bubble lights up.
 *
 * Pre-this-change AudioPlayer DID gate on `enabled` and the
 * SendContext.onSuccess path imperatively played without a
 * timestamp binding - so the stop button never appeared.
 *
 * Renders nothing visible (the actual <audio> element lives
 * inside AudioReplyProvider so context.stop() can address it).
 */
export function AudioPlayer() {
  const lastPlayedAt = useRef<string>('');
  const queryClient = useQueryClient();
  const { playUrl } = useAudioReply();

  useEffect(() => {
    const unsubscribe = queryClient
      .getQueryCache()
      .subscribe((event) => {
        if (event.type !== 'updated') return;
        if (event.query.queryKey[0] !== 'conversation') return;

        const data = event.query.state.data as ConversationResponse | undefined;
        const turns = data?.turns ?? [];
        // Walk from the end - we only ever care about the most
        // recent assistant turn.
        for (let i = turns.length - 1; i >= 0; i--) {
          const t = turns[i];
          if (!t || t.role !== 'assistant') continue;
          const audioUrl = t.audio_url;
          const ts = t.timestamp ?? '';
          if (!audioUrl || !ts) break;
          if (ts === lastPlayedAt.current) break;
          lastPlayedAt.current = ts;
          // Pass the timestamp so playingFor matches and the
          // stop button on this turn's bubble can render.
          playUrl(audioUrl, ts);
          break;
        }
      });
    return unsubscribe;
  }, [queryClient, playUrl]);

  return null;
}
